import { invariant } from '@mimir/common/util/invariant';
import { getAbsoluteStartPositionInSeconds, type ModelAudioTrackBox, type ModelTrackBox, type ModelVideoTrackBox } from '../private/timelineModel';
import type { SourceRef } from '../private/sourceRef';
import type {
  UiAudioBox, UiAudioTrackBox, TimelineUIState, UiImageBox, UiVideoBox,
  UiVideoTrackBox, UiTrackBox, UiAudioTrack,
} from './timelineUIState';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { TimelineStateUpdaterOps } from './timelineStateUpdaterOps';
import { addFractions, compareFractions } from '@mimir/common/util/integerMath';


/**
 * This class contains the operations necessary to
 * update the state used by the UI.
 *
 * For now, it is done by directly mutating a state
 * object that is supposed to have been made reactive by the client
 * code before being passed as a constructor parameter.
 */
export class TimelineUIStateUpdater implements TimelineStateUpdaterOps {


  /**
   * All the boxes, indexed by their box IDs.
   */
  private boxes: Record<string, UiTrackBox> = {};

  /**
   * For each source, whether it is currently dragged or not.
   */
  private sourceDragged: Record<string, boolean> = {};

  /**
   * All the boxes, indexed by source ID.
   */
  private boxesBySourceId: Record<string, UiTrackBox[]> = {};

  constructor(
    private readonly timelineUIState: TimelineUIState,
  ) {}


  addSource(sourceRef: SourceRef) {
    this.boxesBySourceId[sourceRef.srcId] = [];
    this.sourceDragged[sourceRef.srcId] = false;
  }


  removeSource(sourceRef: SourceRef) {
    delete this.boxesBySourceId[sourceRef.srcId];
    delete this.sourceDragged[sourceRef.srcId];
  }


  updateUndoRedoStatus(canUndo: boolean, canRedo: boolean) {
    this.timelineUIState.canUndo = canUndo;
    this.timelineUIState.canRedo = canRedo;
  }


  private convertModelVideoTrackBox(b: ModelVideoTrackBox): UiVideoTrackBox {
    if (b.type === 'video') {
      const box: UiVideoBox = {
        type: 'video',
        boxStartPositionInSeconds: addFractions(b.inPointInSeconds, b.sourceRef.mediaStartOffset),
        inPointInSeconds: b.inPointInSeconds,
        outPointInSeconds: b.outPointInSeconds,
        itemId: b.sourceRef.itemRef.itemId,
        boxId: b.boxId,
        dragged: this.sourceDragged[b.sourceRef.srcId],
      };
      return box;
    } else {
      const box: UiImageBox = {
        type: 'image',
        boxStartPositionInSeconds: addFractions(b.inPointInSeconds, b.sourceRef.mediaStartOffset),
        inPointInSeconds: b.inPointInSeconds,
        outPointInSeconds: b.outPointInSeconds,
        itemId: b.sourceRef.itemRef.itemId,
        boxId: b.boxId,
        dragged: this.sourceDragged[b.sourceRef.srcId],
      };
      return box;
    }
  }


  insertVideoTrackBox(trackIndex: number, index: number, box: ModelVideoTrackBox) {
    const uiBox = this.convertModelVideoTrackBox(box);
    this.timelineUIState.videoTracks[trackIndex].splice(index, 0, uiBox);
    this.boxes[uiBox.boxId] = uiBox;
    this.boxesBySourceId[box.sourceRef.srcId].push(uiBox);
  }


  removeVideoTrackBox(trackIndex: number, box: ModelVideoTrackBox) {
    const uiVideoBoxIndex = this.timelineUIState.videoTracks[trackIndex].findIndex(b => b.boxId === box.boxId);
    invariant(uiVideoBoxIndex !== -1, () => `Could not find UI box ${box.boxId} on video track ${trackIndex}`);
    this.timelineUIState.videoTracks[trackIndex].splice(uiVideoBoxIndex, 1);
    delete this.boxes[box.boxId];

    const index = this.boxesBySourceId[box.sourceRef.srcId].findIndex(b => b.boxId === box.boxId);
    invariant(index !== -1, () => `Could not find box ${box.boxId} for source ${box.sourceRef.srcId}`);
    this.boxesBySourceId[box.sourceRef.srcId].splice(index, 1);
  }


  private convertModelAudioTrackBox(b: ModelAudioTrackBox): UiAudioTrackBox {
    const box: UiAudioBox = {
      type: 'audio',
      inPointInSeconds: b.inPointInSeconds,
      outPointInSeconds: b.outPointInSeconds,
      itemId: b.sourceRef.itemRef.itemId,
      boxId: b.boxId,
      channel: b.channel,
      boxStartPositionInSeconds: getAbsoluteStartPositionInSeconds(b),
      dragged: this.sourceDragged[b.sourceRef.srcId],
    };
    return box;
  }


  addAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox) {
    const uiBox = this.convertModelAudioTrackBox(box);
    this.timelineUIState.audioTracks[trackIndex].push(uiBox);
    this.boxes[uiBox.boxId] = uiBox;
    this.boxesBySourceId[box.sourceRef.srcId].push(uiBox);
    this.sortAudioTrack(this.timelineUIState.audioTracks[trackIndex]);
  }


  removeAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox) {
    const uiAudioBoxIndex = this.timelineUIState.audioTracks[trackIndex].findIndex(b => b.boxId === box.boxId);
    invariant(uiAudioBoxIndex !== -1, () => `Could not find UI box ${box.boxId} on audio track ${trackIndex}`);
    this.timelineUIState.audioTracks[trackIndex].splice(uiAudioBoxIndex, 1);
    delete this.boxes[box.boxId];

    const index = this.boxesBySourceId[box.sourceRef.srcId].findIndex(b => b.boxId === box.boxId);
    invariant(index !== -1, () => `Could not find box ${box.boxId} for source ${box.sourceRef.srcId}`);
    this.boxesBySourceId[box.sourceRef.srcId].splice(index, 1);

    this.sortAudioTrack(this.timelineUIState.audioTracks[trackIndex]);
  }


  addVideoTrack() {
    this.timelineUIState.videoTracks.push([]);
  }


  addAudioTrack() {
    this.timelineUIState.audioTracks.push([]);
  }


  setOutPoint(b: ModelTrackBox, outPoint: Fraction): void {
    const box = this.boxes[b.boxId];
    invariant(box, () => `Could not find UI box ${b.boxId}`);
    switch (box.type) {
      case 'video':
      case 'audio':
      case 'image': {
        box.outPointInSeconds = {
          numerator: outPoint.numerator,
          denominator: outPoint.denominator,
        };
        break;
      }
    }
  }


  setInPoint(b: ModelTrackBox, inPoint: Fraction): void {
    const box = this.boxes[b.boxId];
    invariant(box, () => `Could not find UI box ${b.boxId}`);
    switch (box.type) {
      case 'video':
      case 'audio':
      case 'image': {
        box.inPointInSeconds = {
          numerator: inPoint.numerator,
          denominator: inPoint.denominator,
        };
        break;
      }
    }
  }


  private sortAudioTrack(audioTrack: UiAudioTrack) {
    audioTrack.sort((a: UiAudioBox, b: UiAudioBox) => {
      const cmp = compareFractions(a.boxStartPositionInSeconds, b.boxStartPositionInSeconds);
      if (cmp !== 0) {
        return cmp;
      }
      return a.boxId < b.boxId ? -1 : 1;
    });
  }


  /**
   * Sorts the elements of each audio track by start position then
   * by box ID in case of equal start positions.
   */
  sortTracks() {
    for (const audioTrack of this.timelineUIState.audioTracks) {
      this.sortAudioTrack(audioTrack);
    }
  }


  setSourceStart(sourceRef: SourceRef, start: Fraction): void {
    // Nothing to do
  }


  setBoxStart(b: ModelTrackBox, start: Fraction): void {
    const box = this.boxes[b.boxId];
    invariant(box !== undefined, () => `Could not find box ${b.boxId}`);
    box.boxStartPositionInSeconds = start;
  }


  setSourceDragged(sourceRef: SourceRef, dragged: boolean): void {
    this.sourceDragged[sourceRef.srcId] = dragged;
    const boxes = this.boxesBySourceId[sourceRef.srcId];
    boxes.forEach(b => b.dragged = dragged);
  }

}
